import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { UpdateWorkspace, UpdateWorkspaceLocation } from '../../../../shared/types/workspaceTypes';
import { useKakaoGeocoder } from '../../../../common/components/KakaoMap/useKakaoGeocoder';
import { IconInput } from '../../../../shared/components/IconInput';
import { FlexCol } from '../../../../shared/FlexCol';
import { Body, Headline, Label } from '../../../../shared/styled-components/styled_text';
import { FlexRow } from '../../../../shared/FlexRow';
import { Tag } from '../../../../shared/styled-components/styled_tags';
import { FilledButton } from '../../../../shared/styled-components/styled_buttons';
import { AddressSearch, AddressSearchHandle } from '../../../../common/components/AddressSearch';
import { WarningTooltip } from '../../../../common/components/WarningTooltip';

interface Props {
  data: UpdateWorkspace;
  handleInputClick: (name: string) => void;
  onChange: (data: UpdateWorkspace) => void;
}

export const OnboardingStep1InputArea: React.FC<Props> = ({ data, handleInputClick, onChange }) => {
  const addressSearch = useRef<AddressSearchHandle>(null);
  const geoCoder = useKakaoGeocoder();
  const searchAddress = useCallback(() => addressSearch.current?.open(), []);
  const [locationInfo, setLocationInfo] = useState<UpdateWorkspaceLocation>({
    x: data.location.x,
    y: data.location.y,
    address: data.location.address,
    zoom_lv: data.location.zoom_lv,
    address_detail: data.location.address_detail,
  });

  useEffect(() => {
    onChange({ ...data, location: locationInfo });
  }, [locationInfo]);

  useEffect(() => {
    if (locationInfo.address) {
      geoCoder.current?.addressSearch(locationInfo.address, (result: any[]) => {
        if (result.length) {
          setLocationInfo((prev: UpdateWorkspaceLocation) => ({
            ...prev,
            x: parseFloat(result[0].address.x),
            y: parseFloat(result[0].address.y),
          }));
        }
      });
    }
  }, [locationInfo.address]);

  const handleSelect = (address: string) => {
    setLocationInfo((prev: UpdateWorkspaceLocation) => ({ ...prev, address }));
  };

  return (
    <Container>
      <AddressSearch ref={addressSearch} onSelect={handleSelect} />
      <FlexCol width={'100%'} justify={'flex-start'} align={'flex-start'} gap={'0.8rem'}>
        <FlexRow width={'100%'} justify={'flex-start'} align={'center'} gap={'0.6rem'}>
          <Tag color={'primary'}>필수</Tag>
          <Headline size={'m'}>안내 정보 작성하기</Headline>
        </FlexRow>
        <Body size={'m'} style={{ color: ' var(--content-color-secondary)' }}>
          지원자에게 발송하는 안내 메시지(알림톡/안내 페이지)에 필요해요.
        </Body>
      </FlexCol>
      <FlexCol width={'100%'} justify={'flex-start'} align={'flex-start'} gap={'2.4rem'}>
        <IconInput
          label={<Label size={'m'}>회사 이름</Label>}
          value={data.name ?? ''}
          onClick={() => handleInputClick('company_name')}
          onChange={name => {
            onChange({ ...data, name });
          }}
          placeholder={'이름을 입력해주세요.'}
          max={20}
          showMaxCount
          hint={'최대 20자까지 입력 가능해요.'}
        />
        <IconInput
          label={
            <FlexRow justify={'flex-start'} gap={'0.4rem'}>
              <Label size={'m'}>회사 연락처</Label>
              <WarningTooltip color={'grey'} message={'입력하신 연락처는 알림톡과 상세내용에 사용됩니다.'} size={'1.8rem'} icon={'help'} />
            </FlexRow>
          }
          value={data.contact.officer_contact}
          onClick={() => handleInputClick('company_contact')}
          onChange={officer_contact => onChange({ ...data, contact: { ...data.contact, officer_contact } })}
          id={'contact'}
          autocomplete={false}
          placeholder={"'-' 없이 숫자만 입력해주세요."}
          type={'tel'}
          max={11}
          icons={{
            end2: {
              src: '/images/common/cancel_circle.svg',
              onClick: () => onChange({ ...data, contact: { ...data.contact, officer_contact: '' } }),
              needsInput: true,
            },
          }}
        />
        <LabelContainer label={'회사 주소(찾아오시는 길)'}>
          <FlexRow justify={'flex-start'} gap={'0.4rem'} width={'100%'}>
            <IconInput
              value={data.location.address ?? ''}
              placeholder={'주소를 검색해주세요.'}
              onClick={() => {
                handleInputClick('address');
                searchAddress();
              }}
              onChange={() => undefined}
              style={{ flexGrow: 1 }}
              readonly
              id={'address'}
            />
            <FilledButton
              size={'s'}
              color={'mono'}
              icon={{ slot: 'end', src: '/images/common/search.svg' }}
              onClick={() => {
                handleInputClick('address_search');
                searchAddress();
              }}
            >
              주소 검색
            </FilledButton>
          </FlexRow>
          <IconInput
            value={locationInfo.address_detail ?? ''}
            onClick={() => handleInputClick('detail_address')}
            onChange={address_detail => {
              setLocationInfo((prev: UpdateWorkspaceLocation) => ({
                ...prev,
                address_detail,
              }));
            }}
            placeholder={'(필수)상세주소'}
            id={'address_detail'}
            max={255}
          />
        </LabelContainer>
      </FlexCol>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  min-width: 49.8rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const LabelContainer = styled.div<{ label: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 1.6rem;
  font-weight: 500;
  line-height: 160%;

  &:before {
    content: '${props => props.label}';
  }
`;
